import siteConfig from "./siteConfig.json";

const sharedSupportCards = [
  "Personalized counselling",
  "Strategic university shortlisting",
  "Application and visa support"
];

export const countryPages = {
  usa: {
    slug: "usa",
    name: "USA",
    flag: "🇺🇸",
    mapKey: "usa",
    seo: {
      title: "Study in USA | NextEra Abroad",
      description:
        "Explore top US universities with strategic counselling, admissions guidance, and visa support from NextEra Abroad."
    },
    mood: {
      gradient: "from-blue-600/30 via-sky-400/15 to-warm-gold/20",
      chip: "bg-blue-500/15 text-blue-900 border-blue-500/30",
      accentText: "text-blue-700",
      mapGlow: "text-blue-500"
    },
    hero: {
      headline: "Build a Future-Ready Career with US Education",
      subheadline:
        "From research-led campuses to high-impact industry exposure, the USA offers scale, ambition, and unmatched learning ecosystems.",
      ctaPrimary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      ctaSecondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert },
      supportCards: sharedSupportCards,
      quickFacts: [
        { label: "Top-tier universities", value: "4000+" },
        { label: "Popular intake windows", value: "Fall / Spring" },
        { label: "Career ecosystem", value: "Global" }
      ]
    },
    overview: {
      title: "Why Students Choose the USA",
      description:
        "The USA remains a leading destination for students who want strong academics, interdisciplinary flexibility, and exposure to global employers.",
      points: [
        {
          title: "Academic Depth",
          text: "Specialized programs, strong faculty mentorship, and cutting-edge research environments."
        },
        {
          title: "Career Momentum",
          text: "Internships, networking, and project work aligned with real-world market needs."
        },
        {
          title: "Campus Experience",
          text: "Vibrant student communities, diverse cultures, and supportive campus life services."
        },
        {
          title: "Long-term Value",
          text: "Globally recognized degrees with strong pathways into high-growth industries."
        }
      ]
    },
    cost: {
      tuition: [
        {
          label: "Undergraduate Programs",
          value: "USD 20,000 - 45,000 / year",
          note: "Varies by university type and state"
        },
        {
          label: "Postgraduate Programs",
          value: "USD 22,000 - 55,000 / year",
          note: "STEM and specialist tracks may differ"
        }
      ],
      living: [
        {
          label: "Monthly Living Expenses",
          value: "USD 900 - 2,000 / month",
          note: "Depends on city, housing, and lifestyle"
        },
        {
          label: "Health Insurance + Essentials",
          value: "USD 120 - 300 / month",
          note: "Institution and plan dependent"
        }
      ]
    },
    process: [
      {
        title: "Profile Evaluation",
        text: "We assess academic background, test profile, budget, and outcomes you are targeting."
      },
      {
        title: "University Shortlisting",
        text: "Balanced shortlist across ambitious, target, and safe options with strategic fit."
      },
      {
        title: "Document Preparation",
        text: "SOP strategy, LOR planning, resume refinement, and checklist-driven document readiness."
      },
      {
        title: "Application Submission",
        text: "Portal guidance, deadline tracking, and error-free submission support."
      },
      {
        title: "Offer Support",
        text: "We help compare admits, scholarships, and decision factors before finalizing."
      },
      {
        title: "Visa Guidance",
        text: "Structured documentation support and interview readiness for confident filing."
      }
    ],
    courses: [
      { name: "Computer Science & IT", text: "AI, software engineering, cloud, cybersecurity" },
      { name: "Business & Management", text: "MBA pathways, analytics, consulting, strategy" },
      { name: "Engineering", text: "Mechanical, civil, electrical, and systems innovation" },
      { name: "Data Science & AI", text: "Advanced analytics, machine learning, applied AI" },
      { name: "Healthcare & Public Health", text: "Healthcare systems, biotech, population health" },
      { name: "Finance & Economics", text: "Investment analysis, fintech, corporate finance" }
    ],
    universities: [
      { name: "Massachusetts Institute of Technology", city: "Cambridge", text: "Research-led innovation and technology leadership." },
      { name: "Stanford University", city: "Stanford", text: "Entrepreneurship-driven culture with global influence." },
      { name: "University of California, Berkeley", city: "Berkeley", text: "Academic rigor with strong industry connections." },
      { name: "Carnegie Mellon University", city: "Pittsburgh", text: "Top destination for computing and engineering." },
      { name: "University of Michigan", city: "Ann Arbor", text: "Comprehensive academic ecosystem and strong outcomes." },
      { name: "New York University", city: "New York", text: "Global city exposure and multidisciplinary strengths." }
    ],
    career: {
      title: "Career Prospects After Graduation",
      summary:
        "US education equips students for high-impact roles through internships, project learning, and strong employer networks.",
      points: [
        "Career services support and employer events across campuses",
        "Internship exposure in global companies and startups",
        "Strong demand in technology, analytics, finance, and healthcare",
        "Global mobility value from US academic credentials"
      ]
    },
    lifestyle: {
      title: "Student Life in the USA",
      description:
        "A dynamic environment where students build independence, collaborate across cultures, and develop both academic and professional confidence.",
      highlights: ["Diverse student communities", "High campus engagement", "Innovation-first mindset", "Strong support ecosystems"]
    },
    faq: [
      {
        q: "Do I need GRE or GMAT for the USA?",
        a: "It depends on the program and university. Many schools are test-optional, while others still prefer scores for competitive profiles."
      },
      {
        q: "Can I work part-time while studying?",
        a: "International students can usually work part-time on campus, subject to visa regulations and institutional policy."
      },
      {
        q: "When should I start applications?",
        a: "Ideally 10-14 months before your target intake to allow enough time for test prep, documents, and quality applications."
      },
      {
        q: "How does NextEra support visa preparation?",
        a: "We assist with documentation sequencing, checklist validation, and interview preparation guidance."
      }
    ],
    finalCta: {
      title: "Ready to Plan Your USA Journey with Confidence?",
      subtitle:
        "Let NextEra Abroad help you shortlist smarter, apply strategically, and move ahead with clarity.",
      primary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      secondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert }
    }
  },
  canada: {
    slug: "canada",
    name: "Canada",
    flag: "🇨🇦",
    mapKey: "canada",
    seo: {
      title: "Study in Canada | NextEra Abroad",
      description:
        "Explore Canadian universities with strategic counselling, admission support, and visa guidance from NextEra Abroad."
    },
    mood: {
      gradient: "from-sky-500/25 via-cyan-300/20 to-blue-200/25",
      chip: "bg-sky-500/15 text-sky-900 border-sky-500/30",
      accentText: "text-sky-700",
      mapGlow: "text-sky-500"
    },
    hero: {
      headline: "Study in Canada for Quality Education and Strong Career Pathways",
      subheadline:
        "Canada combines globally respected institutions, a welcoming student environment, and practical opportunities for long-term growth.",
      ctaPrimary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      ctaSecondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert },
      supportCards: sharedSupportCards,
      quickFacts: [
        { label: "Top student destination", value: "Globally Preferred" },
        { label: "Academic quality", value: "High" },
        { label: "Career pathways", value: "Strong" }
      ]
    },
    overview: {
      title: "Why Students Choose Canada",
      description:
        "Canada is a leading destination for students who want quality education, international exposure, and a balanced learning environment.",
      points: [
        {
          title: "Globally Recognized Degrees",
          text: "Canadian qualifications are respected worldwide across major industries."
        },
        {
          title: "Welcoming Student Environment",
          text: "Multicultural campuses and supportive communities help students settle smoothly."
        },
        {
          title: "Career-Oriented Learning",
          text: "Programs often include practical components aligned with employer expectations."
        },
        {
          title: "Long-term Value",
          text: "Strong academic foundations and global employability outcomes."
        }
      ]
    },
    cost: {
      tuition: [
        {
          label: "Undergraduate Programs",
          value: "CAD 18,000 - 35,000 / year",
          note: "Depends on institution, province, and program"
        },
        {
          label: "Postgraduate Programs",
          value: "CAD 19,000 - 40,000 / year",
          note: "Business and specialist programs may vary"
        }
      ],
      living: [
        {
          label: "Monthly Living Expenses",
          value: "CAD 1,000 - 2,200 / month",
          note: "City and accommodation choices influence totals"
        },
        {
          label: "Health + Transport + Utilities",
          value: "CAD 180 - 450 / month",
          note: "Province and lifestyle dependent"
        }
      ]
    },
    process: [
      {
        title: "Profile Evaluation",
        text: "We assess academics, budget, and career goals to map suitable Canadian pathways."
      },
      {
        title: "University and Program Shortlisting",
        text: "Balanced shortlist tailored to competitiveness, affordability, and long-term outcomes."
      },
      {
        title: "Document and SOP Preparation",
        text: "Structured support for statements, references, and complete application documents."
      },
      {
        title: "Application Submission",
        text: "Step-by-step portal support and timeline management for error-free submissions."
      },
      {
        title: "Offer and Financial Planning",
        text: "Guidance on admit decisions, tuition planning, and next-step readiness."
      },
      {
        title: "Visa Guidance",
        text: "Practical documentation guidance and process support for confident visa filing."
      }
    ],
    courses: [
      { name: "Computer Science & IT", text: "Software systems, AI, cloud, and data technologies" },
      { name: "Business & Management", text: "Global business, analytics, leadership, and strategy" },
      { name: "Engineering", text: "Mechanical, civil, electrical, and advanced systems" },
      { name: "Healthcare & Public Health", text: "Health systems, clinical pathways, and policy focus" },
      { name: "Data Science & Analytics", text: "Applied analytics, modeling, and decision intelligence" },
      { name: "Media & Communication", text: "Digital communication and creative industries" }
    ],
    universities: [
      { name: "University of Toronto", city: "Toronto", text: "Research leadership with global academic recognition." },
      { name: "University of British Columbia", city: "Vancouver", text: "Strong multidisciplinary excellence and global exposure." },
      { name: "McGill University", city: "Montreal", text: "Prestigious institution with rich international student culture." },
      { name: "University of Alberta", city: "Edmonton", text: "Research-driven education with strong technical pathways." },
      { name: "University of Waterloo", city: "Waterloo", text: "Known for innovation-focused programs and industry links." },
      { name: "Western University", city: "London", text: "Comprehensive programs with strong student experience." }
    ],
    career: {
      title: "Career Prospects After Canadian Study",
      summary:
        "Canada offers strong employability outcomes through practical programs, employer connections, and globally transferable skills.",
      points: [
        "High demand for skilled graduates in technology, healthcare, and business",
        "Strong internship and work-integrated learning opportunities",
        "Supportive career services across major institutions",
        "Long-term global mobility value from Canadian credentials"
      ]
    },
    lifestyle: {
      title: "Student Life in Canada",
      description:
        "Students experience multicultural communities, safe cities, and a balanced lifestyle that supports academic and personal growth.",
      highlights: ["Welcoming communities", "High quality of life", "Inclusive campuses", "Strong student support"]
    },
    faq: [
      {
        q: "Is Canada a good destination for international students?",
        a: "Yes, Canada is known for quality education, supportive student environments, and strong career-focused programs."
      },
      {
        q: "When should I start my Canada applications?",
        a: "Starting 8-12 months in advance is ideal for strong shortlisting, documentation, and timely submissions."
      },
      {
        q: "Are scholarships available in Canada?",
        a: "Scholarships vary by university and program. We help identify options based on your profile."
      },
      {
        q: "How does NextEra support the visa process?",
        a: "We assist with documentation sequencing, checklist validation, and practical process guidance."
      }
    ],
    finalCta: {
      title: "Plan Your Canada Journey with Clarity and Confidence",
      subtitle:
        "Get a personalized roadmap for Canadian universities, applications, and visa preparation with NextEra Abroad.",
      primary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      secondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert }
    }
  },
  uk: {
    slug: "uk",
    name: "UK",
    flag: "🇬🇧",
    mapKey: "uk",
    seo: {
      title: "Study in UK | NextEra Abroad",
      description:
        "Explore UK universities with premium counselling, admissions planning, and complete visa guidance from NextEra Abroad."
    },
    mood: {
      gradient: "from-indigo-600/30 via-blue-300/15 to-sky-200/30",
      chip: "bg-indigo-500/15 text-indigo-900 border-indigo-500/30",
      accentText: "text-indigo-700",
      mapGlow: "text-indigo-500"
    },
    hero: {
      eyebrow: "Prestige and Academic Heritage",
      headline: "Earn a Globally Respected Degree in the UK",
      subheadline:
        "The UK combines academic excellence, focused degree pathways, and strong student life in one of the world’s most trusted education destinations.",
      ctaPrimary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      ctaSecondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert },
      supportCards: sharedSupportCards,
      quickFacts: [
        { label: "Typical master’s duration", value: "1 Year" },
        { label: "Top institutions", value: "Historic + Modern" },
        { label: "Student cities", value: "Vibrant" }
      ]
    },
    overview: {
      title: "Why Students Choose the UK",
      description:
        "The UK is ideal for students who want quality education, efficient timelines, and a high-value international qualification.",
      points: [
        {
          title: "Prestigious Institutions",
          text: "Home to globally respected universities known for academic rigor."
        },
        {
          title: "Time-Efficient Degrees",
          text: "Shorter degree structures can help reduce overall education cost and time."
        },
        {
          title: "Career Exposure",
          text: "Strong links with finance, consulting, media, healthcare, and tech sectors."
        },
        {
          title: "Global Environment",
          text: "A multicultural student ecosystem that supports global networking."
        }
      ]
    },
    cost: {
      tuition: [
        {
          label: "Undergraduate Programs",
          value: "GBP 12,000 - 30,000 / year",
          note: "Course and university dependent"
        },
        {
          label: "Postgraduate Programs",
          value: "GBP 14,000 - 35,000 / year",
          note: "Business and specialist tracks may be higher"
        }
      ],
      living: [
        {
          label: "Monthly Living Expenses",
          value: "GBP 800 - 1,600 / month",
          note: "London and major cities can be higher"
        },
        {
          label: "Utilities + Local Transport",
          value: "GBP 120 - 280 / month",
          note: "Varies by city and housing"
        }
      ]
    },
    process: [
      {
        title: "Initial Strategy Session",
        text: "We identify the right programs and universities around your profile and ambitions."
      },
      {
        title: "Shortlisting and Fit Analysis",
        text: "Balanced shortlist with strong academic and career alignment."
      },
      {
        title: "Application and Statement Planning",
        text: "We support personal statement quality, references, and document readiness."
      },
      {
        title: "Submission and Offer Tracking",
        text: "Structured timelines and follow-up support through offer stages."
      },
      {
        title: "Financial and CAS Readiness",
        text: "Guidance on key admission confirmations and fund preparedness."
      },
      {
        title: "Visa Guidance",
        text: "Complete support through documentation and application workflow."
      }
    ],
    courses: [
      { name: "Business & Management", text: "Management, finance, marketing, leadership" },
      { name: "Law", text: "International law, corporate law, legal studies" },
      { name: "Data Analytics", text: "Applied analytics, business intelligence" },
      { name: "Engineering", text: "Civil, mechanical, and advanced systems" },
      { name: "Media & Communication", text: "Digital media, journalism, strategic communication" },
      { name: "Public Health", text: "Global health systems and policy" }
    ],
    universities: [
      { name: "University of Oxford", city: "Oxford", text: "Research depth and global academic prestige." },
      { name: "University of Cambridge", city: "Cambridge", text: "Historic excellence across disciplines." },
      { name: "Imperial College London", city: "London", text: "Strong focus on STEM and innovation." },
      { name: "London School of Economics", city: "London", text: "Global reputation in social sciences and economics." },
      { name: "University of Manchester", city: "Manchester", text: "Large research university with vibrant student life." },
      { name: "University of Edinburgh", city: "Edinburgh", text: "Top-ranked teaching in a culturally rich city." }
    ],
    career: {
      title: "Career Prospects After UK Study",
      summary:
        "UK universities offer strong employability ecosystems, global employer recognition, and industry-facing learning outcomes.",
      points: [
        "High graduate employability in global markets",
        "Strong internship and networking ecosystems",
        "Popular sectors: finance, consulting, tech, media, healthcare",
        "Career pathways shaped by practical academic structure"
      ]
    },
    lifestyle: {
      title: "Student Life in the UK",
      description:
        "From historic university towns to energetic global cities, students enjoy rich culture, active societies, and balanced independent living.",
      highlights: ["Strong student unions", "Cultural diversity", "Rich history and arts", "Excellent transport access"]
    },
    faq: [
      {
        q: "Is one-year master’s in the UK a good option?",
        a: "Yes, many students prefer it for focused learning and faster career transition, depending on their goals."
      },
      {
        q: "Do I need IELTS for UK universities?",
        a: "Language requirements vary. Some institutions accept alternatives or waivers based on prior education."
      },
      {
        q: "How early should I apply for UK intakes?",
        a: "Starting 8-12 months early is ideal to secure better university options and manage visa timelines smoothly."
      },
      {
        q: "Can NextEra guide document preparation?",
        a: "Yes, we assist with statement strategy, references, and submission checklists."
      }
    ],
    finalCta: {
      title: "Make Your UK Plan Clear and Confident",
      subtitle:
        "Get a strategic roadmap for universities, applications, and visa preparation with NextEra Abroad.",
      primary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      secondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert }
    }
  },
  australia: {
    slug: "australia",
    name: "Australia",
    flag: "🇦🇺",
    mapKey: "australia",
    seo: {
      title: "Study in Australia | NextEra Abroad",
      description:
        "Discover Australian universities with personalized counselling, application support, and visa guidance from NextEra Abroad."
    },
    mood: {
      gradient: "from-cyan-500/25 via-emerald-300/20 to-blue-300/30",
      chip: "bg-cyan-500/15 text-cyan-900 border-cyan-500/30",
      accentText: "text-cyan-700",
      mapGlow: "text-cyan-500"
    },
    hero: {
      eyebrow: "Lifestyle Meets Academic Quality",
      headline: "Study in Australia for Balance, Growth, and Global Exposure",
      subheadline:
        "Australia offers modern education, welcoming campuses, and a high-quality student lifestyle with strong career relevance.",
      ctaPrimary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      ctaSecondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert },
      supportCards: sharedSupportCards,
      quickFacts: [
        { label: "Student-friendly cities", value: "High" },
        { label: "Education quality", value: "World-class" },
        { label: "Campus lifestyle", value: "Vibrant" }
      ]
    },
    overview: {
      title: "Why Students Choose Australia",
      description:
        "Australia is a top destination for students seeking practical education, multicultural campuses, and a lifestyle-driven study experience.",
      points: [
        {
          title: "Modern Learning",
          text: "Programs emphasize practical outcomes, projects, and industry relevance."
        },
        {
          title: "Quality of Life",
          text: "Safe cities, good infrastructure, and strong student wellbeing support."
        },
        {
          title: "Global Classrooms",
          text: "Diverse cohorts that build cultural confidence and networks."
        },
        {
          title: "Career Orientation",
          text: "Strong university-employer alignment in key sectors."
        }
      ]
    },
    cost: {
      tuition: [
        {
          label: "Undergraduate Programs",
          value: "AUD 20,000 - 45,000 / year",
          note: "Course and institution dependent"
        },
        {
          label: "Postgraduate Programs",
          value: "AUD 22,000 - 50,000 / year",
          note: "Premium programs may vary"
        }
      ],
      living: [
        {
          label: "Monthly Living Expenses",
          value: "AUD 1,400 - 2,800 / month",
          note: "City and accommodation type influence total"
        },
        {
          label: "Transport + Utilities",
          value: "AUD 180 - 420 / month",
          note: "Depends on city and commuting style"
        }
      ]
    },
    process: [
      {
        title: "Counselling and Course Match",
        text: "We identify universities and courses aligned with your academic and career goals."
      },
      {
        title: "Shortlisting",
        text: "Balanced shortlist built for outcomes, affordability, and competitiveness."
      },
      {
        title: "Document and SOP Support",
        text: "Structured guidance for statement quality and complete documentation."
      },
      {
        title: "Application Execution",
        text: "Submission workflow, tracking, and proactive timeline support."
      },
      {
        title: "Offer Decision Support",
        text: "Help comparing admits, fee structures, and practical student fit."
      },
      {
        title: "Visa Preparation",
        text: "Documentation clarity and process support for confident filing."
      }
    ],
    courses: [
      { name: "Information Technology", text: "Software, networks, cybersecurity, cloud" },
      { name: "Business & Commerce", text: "Global business, analytics, leadership" },
      { name: "Engineering", text: "Civil, mechanical, environmental systems" },
      { name: "Healthcare & Nursing", text: "Clinical pathways and public health" },
      { name: "Hospitality & Tourism", text: "Global hospitality and service leadership" },
      { name: "Design & Creative Arts", text: "Digital design, media, creative production" }
    ],
    universities: [
      { name: "University of Melbourne", city: "Melbourne", text: "Academic excellence with strong global recognition." },
      { name: "University of Sydney", city: "Sydney", text: "Research strength and city-based professional exposure." },
      { name: "UNSW Sydney", city: "Sydney", text: "Strong industry links and practical outcomes." },
      { name: "Monash University", city: "Melbourne", text: "Large, globally connected education ecosystem." },
      { name: "University of Queensland", city: "Brisbane", text: "Research-led learning with strong student support." },
      { name: "Australian National University", city: "Canberra", text: "High academic standards and policy relevance." }
    ],
    career: {
      title: "Career Prospects After Australian Study",
      summary:
        "Australian education supports employability through practical learning, internship exposure, and strong skill development.",
      points: [
        "Industry-oriented curriculum and project exposure",
        "Part-time work and networking opportunities while studying",
        "Strong demand in IT, healthcare, engineering, and business",
        "High employer trust in Australian qualifications"
      ]
    },
    lifestyle: {
      title: "Student Life in Australia",
      description:
        "Students enjoy a balanced lifestyle with strong academics, outdoor culture, and inclusive, multicultural communities.",
      highlights: ["Safe and welcoming cities", "Strong campus communities", "High quality of life", "Global student diversity"]
    },
    faq: [
      {
        q: "Is Australia good for international students?",
        a: "Yes. It is known for quality education, inclusive campuses, and strong student support services."
      },
      {
        q: "What intakes are available in Australia?",
        a: "Most universities offer multiple intakes, with February and July commonly used for major admissions cycles."
      },
      {
        q: "How much financial proof is needed?",
        a: "Requirements vary by visa and profile. We guide you through current documentation expectations."
      },
      {
        q: "How does NextEra help after admit decisions?",
        a: "We assist with decision strategy, pre-visa checklist readiness, and next-step planning."
      }
    ],
    finalCta: {
      title: "Turn Your Australia Plan into a Confident Decision",
      subtitle:
        "Work with NextEra Abroad to choose the right path and move ahead with clear expert support.",
      primary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      secondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert }
    }
  },
  europe: {
    slug: "europe",
    name: "Europe",
    flag: "🇪🇺",
    mapKey: "europe",
    seo: {
      title: "Study in Europe | NextEra Abroad",
      description:
        "Explore diverse and high-value study opportunities across Europe with strategic guidance from NextEra Abroad."
    },
    mood: {
      gradient: "from-violet-600/25 via-blue-300/15 to-amber-200/30",
      chip: "bg-violet-500/15 text-violet-900 border-violet-500/30",
      accentText: "text-violet-700",
      mapGlow: "text-violet-500"
    },
    hero: {
      eyebrow: "Diverse and High-Value Opportunities",
      headline: "Explore Europe for Quality Education Across Multiple Destinations",
      subheadline:
        "Europe offers a broad mix of affordable options, globally recognized universities, and rich cross-cultural student experiences.",
      ctaPrimary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      ctaSecondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert },
      supportCards: sharedSupportCards,
      quickFacts: [
        { label: "Destination variety", value: "Wide" },
        { label: "Program choices", value: "Extensive" },
        { label: "Cost flexibility", value: "Strong" }
      ]
    },
    overview: {
      title: "Why Students Choose Europe",
      description:
        "Europe gives students the freedom to choose between different education models, tuition ranges, and cultural experiences while maintaining academic quality.",
      points: [
        {
          title: "Destination Diversity",
          text: "Students can choose between countries based on budget, language, and career goals."
        },
        {
          title: "Academic Value",
          text: "Many programs combine high education standards with cost-effective structures."
        },
        {
          title: "Cultural Exposure",
          text: "Cross-border learning and multicultural networks enrich personal growth."
        },
        {
          title: "Career Reach",
          text: "Internationally relevant qualifications across multiple sectors."
        }
      ]
    },
    cost: {
      tuition: [
        {
          label: "Public/Value-Focused Options",
          value: "EUR 0 - 8,000 / year",
          note: "Available in selected countries and programs"
        },
        {
          label: "Private/Specialized Programs",
          value: "EUR 8,000 - 25,000 / year",
          note: "Depends on institution and discipline"
        }
      ],
      living: [
        {
          label: "Monthly Living Expenses",
          value: "EUR 700 - 1,600 / month",
          note: "Varies significantly by country and city"
        },
        {
          label: "Transport + Essentials",
          value: "EUR 90 - 250 / month",
          note: "Public transit and lifestyle dependent"
        }
      ]
    },
    process: [
      {
        title: "Country Fit Evaluation",
        text: "We compare education quality, tuition, and lifestyle factors across European destinations."
      },
      {
        title: "Program and University Match",
        text: "Shortlisting based on language preference, intake timelines, and career relevance."
      },
      {
        title: "Document Alignment",
        text: "Country-specific checklist planning and application readiness support."
      },
      {
        title: "Application Execution",
        text: "Guided submission process across single or multiple destination choices."
      },
      {
        title: "Admission Decision Support",
        text: "We help prioritize offers based on cost, fit, and long-term return."
      },
      {
        title: "Visa and Pre-departure Guidance",
        text: "End-to-end support tailored to the final selected country."
      }
    ],
    courses: [
      { name: "Business & International Management", text: "Global business strategy and cross-border operations" },
      { name: "Engineering & Technology", text: "Applied engineering and technical innovation" },
      { name: "Data Science & AI", text: "Analytics, ML, and digital transformation" },
      { name: "Hospitality & Tourism", text: "Global hospitality and tourism management" },
      { name: "Design & Creative Disciplines", text: "Architecture, product, and communication design" },
      { name: "Sustainability & Environmental Studies", text: "Climate, policy, and green innovation" }
    ],
    universities: [
      { name: "ETH Zurich", city: "Zurich", text: "Top engineering and science-focused institution." },
      { name: "KU Leuven", city: "Leuven", text: "Research-oriented university with global student appeal." },
      { name: "University of Amsterdam", city: "Amsterdam", text: "Strong multidisciplinary and international programs." },
      { name: "Sorbonne University", city: "Paris", text: "Historic depth and strong modern research output." },
      { name: "University of Copenhagen", city: "Copenhagen", text: "High-quality teaching with global recognition." },
      { name: "Politecnico di Milano", city: "Milan", text: "Leading destination for engineering and design." }
    ],
    career: {
      title: "Career Prospects Across Europe",
      summary:
        "European study can open multiple pathways through cross-cultural work environments, innovation sectors, and regionally diverse opportunities.",
      points: [
        "Access to broad regional opportunities across multiple industries",
        "Strong demand for skilled graduates in technology and management",
        "International work culture and multilingual exposure",
        "Career pathways aligned with practical and research tracks"
      ]
    },
    lifestyle: {
      title: "Student Life in Europe",
      description:
        "From historic academic cities to modern innovation hubs, Europe offers rich cultural life and broad student experiences.",
      highlights: ["Cross-country travel exposure", "Cultural richness", "Diverse campus communities", "Balanced academic lifestyle"]
    },
    faq: [
      {
        q: "Is Europe affordable for international students?",
        a: "Many European destinations offer cost-effective tuition and manageable living ranges compared to other global markets."
      },
      {
        q: "Do I need to know local languages?",
        a: "Many programs are available in English, though local language skills can help with day-to-day life and employability."
      },
      {
        q: "How do I choose the right European country?",
        a: "We compare tuition, course options, language, and career goals to guide your decision."
      },
      {
        q: "Can NextEra help with country-specific visa steps?",
        a: "Yes, we support you with destination-specific documentation and process guidance."
      }
    ],
    finalCta: {
      title: "Discover the Right Europe Path for You",
      subtitle:
        "Get a customized shortlist and expert guidance to choose the best-fit destination across Europe.",
      primary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      secondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert }
    }
  },
  ireland: {
    slug: "ireland",
    name: "Ireland",
    flag: "🇮🇪",
    mapKey: "ireland",
    seo: {
      title: "Study in Ireland | NextEra Abroad",
      description:
        "Plan your Ireland education journey with strategic counselling, admission support, and visa guidance from NextEra Abroad."
    },
    mood: {
      gradient: "from-emerald-600/30 via-teal-300/20 to-sky-200/25",
      chip: "bg-emerald-500/15 text-emerald-900 border-emerald-500/30",
      accentText: "text-emerald-700",
      mapGlow: "text-emerald-500"
    },
    hero: {
      eyebrow: "Tech Growth and Student-Friendly Environment",
      headline: "Choose Ireland for Global Learning in a Fast-Growing Economy",
      subheadline:
        "Ireland combines quality universities, strong tech and business opportunities, and a welcoming student culture.",
      ctaPrimary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      ctaSecondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert },
      supportCards: sharedSupportCards,
      quickFacts: [
        { label: "Tech ecosystem", value: "Strong" },
        { label: "Academic quality", value: "High" },
        { label: "Student appeal", value: "Welcoming" }
      ]
    },
    overview: {
      title: "Why Students Choose Ireland",
      description:
        "Ireland is increasingly preferred by students who want quality education and access to dynamic technology and business ecosystems.",
      points: [
        {
          title: "Industry Presence",
          text: "Strong ecosystem of global firms in tech, pharma, and finance."
        },
        {
          title: "Academic Excellence",
          text: "Well-recognized institutions with modern, industry-aligned curricula."
        },
        {
          title: "Student-Centered Culture",
          text: "Friendly environment and strong support for international learners."
        },
        {
          title: "Career Positioning",
          text: "High relevance for students targeting global business and technology sectors."
        }
      ]
    },
    cost: {
      tuition: [
        {
          label: "Undergraduate Programs",
          value: "EUR 10,000 - 25,000 / year",
          note: "Varies by discipline and institution"
        },
        {
          label: "Postgraduate Programs",
          value: "EUR 12,000 - 30,000 / year",
          note: "Business and specialized courses can differ"
        }
      ],
      living: [
        {
          label: "Monthly Living Expenses",
          value: "EUR 900 - 1,700 / month",
          note: "City and housing choices affect total"
        },
        {
          label: "Utilities + Travel",
          value: "EUR 100 - 260 / month",
          note: "Depends on lifestyle and city"
        }
      ]
    },
    process: [
      {
        title: "Profile and Goal Mapping",
        text: "We align your profile with Irish universities and suitable course pathways."
      },
      {
        title: "Strategic Shortlisting",
        text: "University list based on quality, outcomes, and budget alignment."
      },
      {
        title: "Application Documentation",
        text: "Statement strategy, references, and complete checklist support."
      },
      {
        title: "Submission and Offer Follow-up",
        text: "Application tracking and timely communication through each stage."
      },
      {
        title: "Offer Evaluation",
        text: "Guidance on selecting the best-fit admit for long-term goals."
      },
      {
        title: "Visa Process Guidance",
        text: "Practical support across documentation and process readiness."
      }
    ],
    courses: [
      { name: "Data Analytics & AI", text: "Analytics-driven learning with practical orientation" },
      { name: "Computer Science", text: "Software, systems, and applied computing" },
      { name: "Business & Finance", text: "International business and financial management" },
      { name: "Pharmaceutical Sciences", text: "Drug development and life science pathways" },
      { name: "Cybersecurity", text: "Security architecture and risk management" },
      { name: "Digital Marketing", text: "Performance marketing and growth strategy" }
    ],
    universities: [
      { name: "Trinity College Dublin", city: "Dublin", text: "Prestigious institution with global academic reputation." },
      { name: "University College Dublin", city: "Dublin", text: "Research and career-oriented learning ecosystem." },
      { name: "University of Galway", city: "Galway", text: "Strong student community with multidisciplinary strengths." },
      { name: "Dublin City University", city: "Dublin", text: "Industry-linked programs and modern campus culture." },
      { name: "University College Cork", city: "Cork", text: "High academic quality and strong student support." },
      { name: "Technological University Dublin", city: "Dublin", text: "Applied, skills-focused learning pathways." }
    ],
    career: {
      title: "Career Prospects in Ireland",
      summary:
        "Ireland’s growth in technology, finance, and life sciences creates strong opportunities for globally skilled graduates.",
      points: [
        "Strong ecosystem of multinational employers",
        "Growing demand in data, tech, life sciences, and finance",
        "Practical, job-oriented postgraduate pathways",
        "Supportive graduate employability environment"
      ]
    },
    lifestyle: {
      title: "Student Life in Ireland",
      description:
        "Ireland offers a warm social culture, vibrant student communities, and a balanced environment for academic and personal growth.",
      highlights: ["Welcoming communities", "Rich cultural identity", "Compact student cities", "Balanced pace of life"]
    },
    faq: [
      {
        q: "Is Ireland good for tech-focused students?",
        a: "Yes, Ireland is a strong destination for tech, analytics, and digital business pathways."
      },
      {
        q: "Are there one-year programs in Ireland?",
        a: "Many postgraduate options are available in one-year structures depending on university and discipline."
      },
      {
        q: "How early should I start my Ireland plan?",
        a: "Starting 8-12 months before intake helps secure better options and smoother visa preparation."
      },
      {
        q: "Can NextEra help with shortlist and timelines?",
        a: "Absolutely. We provide complete planning support from shortlist to visa readiness."
      }
    ],
    finalCta: {
      title: "Plan Your Ireland Journey with Expert Clarity",
      subtitle:
        "Get personalized counselling and a practical roadmap to study in Ireland with confidence.",
      primary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      secondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert }
    }
  },
  germany: {
    slug: "germany",
    name: "Germany",
    flag: "🇩🇪",
    mapKey: "germany",
    seo: {
      title: "Study in Germany | NextEra Abroad",
      description:
        "Study in Germany with strategic counselling, admission support, and complete planning guidance from NextEra Abroad."
    },
    mood: {
      gradient: "from-amber-500/30 via-orange-300/15 to-rose-300/25",
      chip: "bg-amber-500/15 text-amber-900 border-amber-500/30",
      accentText: "text-amber-700",
      mapGlow: "text-amber-500"
    },
    hero: {
      eyebrow: "Technical Excellence and Long-Term Value",
      headline: "Study in Germany for Engineering Strength and High Academic Value",
      subheadline:
        "Germany is known for strong technical education, research quality, and cost-efficient pathways for globally ambitious students.",
      ctaPrimary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      ctaSecondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert },
      supportCards: sharedSupportCards,
      quickFacts: [
        { label: "Engineering reputation", value: "Excellent" },
        { label: "Academic value", value: "High" },
        { label: "Research focus", value: "Strong" }
      ]
    },
    overview: {
      title: "Why Students Choose Germany",
      description:
        "Germany is preferred for technical disciplines, research culture, and education pathways that offer strong value for long-term career growth.",
      points: [
        {
          title: "Technical Leadership",
          text: "Highly respected engineering and applied science education ecosystems."
        },
        {
          title: "Research Orientation",
          text: "Strong focus on innovation, labs, and industry-linked learning."
        },
        {
          title: "Cost Advantage",
          text: "Competitive tuition structures in many institutions and programs."
        },
        {
          title: "Industrial Relevance",
          text: "Robust opportunities in manufacturing, automotive, and advanced tech sectors."
        }
      ]
    },
    cost: {
      tuition: [
        {
          label: "Public University Pathways",
          value: "EUR 0 - 3,000 / year",
          note: "Program and state policies vary"
        },
        {
          label: "Private / Specialized Programs",
          value: "EUR 8,000 - 20,000 / year",
          note: "Depends on institution and specialization"
        }
      ],
      living: [
        {
          label: "Monthly Living Expenses",
          value: "EUR 850 - 1,500 / month",
          note: "Housing and city selection impact costs"
        },
        {
          label: "Insurance + Utilities",
          value: "EUR 130 - 320 / month",
          note: "Approximate recurring expenses"
        }
      ]
    },
    process: [
      {
        title: "Academic and Profile Evaluation",
        text: "We assess your profile against country-specific program requirements."
      },
      {
        title: "Course and University Fit",
        text: "Strategic shortlist based on academic quality, budget, and career goals."
      },
      {
        title: "Document and Eligibility Checks",
        text: "Complete support on transcripts, statements, and required documentation."
      },
      {
        title: "Application Submission",
        text: "Step-by-step guidance for university portals and deadline management."
      },
      {
        title: "Admission and Financial Readiness",
        text: "Offer comparison and planning for financial and pre-visa steps."
      },
      {
        title: "Visa Guidance",
        text: "Comprehensive support for documentation and submission readiness."
      }
    ],
    courses: [
      { name: "Mechanical Engineering", text: "Core engineering with industrial application" },
      { name: "Automotive Engineering", text: "Vehicle systems, design, and mobility innovation" },
      { name: "Computer Science", text: "Software systems, data, and applied computing" },
      { name: "Data Science & AI", text: "AI-driven analytics and intelligent systems" },
      { name: "Renewable Energy", text: "Sustainability, energy systems, green technologies" },
      { name: "Business Analytics", text: "Decision science and business intelligence" }
    ],
    universities: [
      { name: "Technical University of Munich", city: "Munich", text: "Global reputation in engineering and technology." },
      { name: "RWTH Aachen University", city: "Aachen", text: "Strong technical focus and industry relevance." },
      { name: "Heidelberg University", city: "Heidelberg", text: "Historic institution with research excellence." },
      { name: "Karlsruhe Institute of Technology", city: "Karlsruhe", text: "Leading destination for engineering and applied sciences." },
      { name: "University of Stuttgart", city: "Stuttgart", text: "Strong mechanical and automotive strengths." },
      { name: "TU Berlin", city: "Berlin", text: "Innovation-driven academic environment in a global city." }
    ],
    career: {
      title: "Career Prospects in Germany",
      summary:
        "Germany offers strong prospects in engineering, manufacturing, automation, and applied technology sectors.",
      points: [
        "High demand for technical and analytical talent",
        "Strong industry-academic collaboration",
        "Research and innovation-focused career pathways",
        "Robust long-term career value from German qualifications"
      ]
    },
    lifestyle: {
      title: "Student Life in Germany",
      description:
        "Students experience a structured academic environment, high public infrastructure quality, and exposure to innovation-driven ecosystems.",
      highlights: ["Strong public systems", "Research-led culture", "Diverse international communities", "High-value student lifestyle"]
    },
    faq: [
      {
        q: "Is Germany mainly for engineering students?",
        a: "Germany is especially strong in engineering, but it also offers excellent options in IT, business, and analytics."
      },
      {
        q: "Are there affordable study options in Germany?",
        a: "Yes, many pathways offer strong academic value with comparatively lower tuition structures."
      },
      {
        q: "Do I need German language skills?",
        a: "Many programs are available in English, though basic German can improve daily life and opportunities."
      },
      {
        q: "How does NextEra support the process?",
        a: "We guide you from profile strategy and shortlisting to applications and visa readiness."
      }
    ],
    finalCta: {
      title: "Build Your Germany Plan with Strategic Guidance",
      subtitle:
        "Start with a personalized roadmap to study in Germany with clarity, confidence, and expert support.",
      primary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      secondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert }
    }
  },
  uae: {
    slug: "uae",
    name: "UAE",
    flag: "🇦🇪",
    mapKey: "uae",
    heroImage: siteConfig.images.destinations.uae,
    heroImageAlt: "Dubai skyline at sunset",
    seo: {
      title: "Study in UAE | NextEra Abroad",
      description:
        "Explore UAE universities with strategic counselling, admissions guidance, and visa support from NextEra Abroad."
    },
    mood: {
      gradient: "from-amber-500/25 via-orange-300/20 to-yellow-200/25",
      chip: "bg-amber-500/15 text-amber-900 border-amber-500/30",
      accentText: "text-amber-700",
      mapGlow: "text-amber-500"
    },
    hero: {
      headline: "Study in UAE for Global Exposure and Modern Campuses",
      subheadline:
        "The UAE offers international branch campuses, industry-aligned programs, and a dynamic multicultural learning environment.",
      ctaPrimary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      ctaSecondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert },
      supportCards: sharedSupportCards,
      quickFacts: [
        { label: "Global branch campuses", value: "Many" },
        { label: "Popular intakes", value: "Sep / Jan" },
        { label: "Campus ecosystem", value: "Modern" }
      ]
    },
    overview: {
      title: "Why Students Choose the UAE",
      description:
        "Students choose the UAE for globally connected education, modern infrastructure, and proximity to fast-growing career markets.",
      points: [
        {
          title: "International Learning Environment",
          text: "Multicultural campuses with globally benchmarked teaching standards."
        },
        {
          title: "Industry-Relevant Programs",
          text: "Courses designed for business, technology, hospitality, and management careers."
        },
        {
          title: "Urban Student Lifestyle",
          text: "High-quality city infrastructure with strong student amenities."
        },
        {
          title: "Regional Career Gateway",
          text: "Excellent networking exposure across Middle East and global companies."
        }
      ]
    },
    cost: {
      tuition: [
        {
          label: "Undergraduate Programs",
          value: "AED 35,000 - 80,000 / year",
          note: "Varies by university and specialization"
        },
        {
          label: "Postgraduate Programs",
          value: "AED 45,000 - 95,000 / year",
          note: "MBA and specialist tracks may be higher"
        }
      ],
      living: [
        {
          label: "Monthly Living Expenses",
          value: "AED 2,800 - 5,500 / month",
          note: "Depends on city, accommodation, and lifestyle"
        },
        {
          label: "Transport + Essentials",
          value: "AED 500 - 1,200 / month",
          note: "Approximate recurring expenses"
        }
      ]
    },
    process: [
      {
        title: "Profile Assessment",
        text: "We map your academics, budget, and target programs against UAE options."
      },
      {
        title: "University Shortlisting",
        text: "Balanced shortlist across branch campuses and local universities with fit-based filtering."
      },
      {
        title: "Document Preparation",
        text: "Structured support for statements, records, and required academic documents."
      },
      {
        title: "Application Submission",
        text: "Portal-level guidance with deadline planning and error-free submissions."
      },
      {
        title: "Offer and Financial Planning",
        text: "Offer comparison and cost planning for tuition, living, and timeline readiness."
      },
      {
        title: "Visa Guidance",
        text: "Step-by-step support for UAE student visa documentation and process readiness."
      }
    ],
    courses: [
      { name: "Business Management", text: "Global business strategy, marketing, and operations" },
      { name: "Computer Science", text: "Software systems, cloud, and data-focused pathways" },
      { name: "Hospitality & Tourism", text: "Service leadership for global hospitality careers" },
      { name: "Finance", text: "Banking, corporate finance, and fintech-adjacent roles" },
      { name: "Engineering", text: "Civil, mechanical, and industrial-focused pathways" },
      { name: "Media & Communication", text: "Digital communication and creative industry programs" }
    ],
    universities: [
      { name: "University of Birmingham Dubai", city: "Dubai", text: "UK-quality academics in a global city setting." },
      { name: "Middlesex University Dubai", city: "Dubai", text: "Strong international student ecosystem and career-focused programs." },
      { name: "Heriot-Watt University Dubai", city: "Dubai", text: "Well-known for engineering, computing, and business pathways." },
      { name: "University of Sharjah", city: "Sharjah", text: "Comprehensive programs with strong regional academic reputation." },
      { name: "American University of Sharjah", city: "Sharjah", text: "Liberal arts and engineering strengths with global exposure." },
      { name: "Abu Dhabi University", city: "Abu Dhabi", text: "Career-oriented programs with strong student support services." }
    ],
    career: {
      title: "Career Prospects After Studying in UAE",
      summary:
        "UAE graduates benefit from strong regional business exposure and industry-oriented learning environments.",
      points: [
        "Access to multinational company ecosystems",
        "Growing demand in business, technology, and services sectors",
        "Strong networking opportunities in major global events and hubs",
        "Practical, career-aligned program structures"
      ]
    },
    lifestyle: {
      title: "Student Life in the UAE",
      description:
        "Students experience a fast-paced, multicultural lifestyle with modern infrastructure and globally connected communities.",
      highlights: ["Modern city living", "Multicultural communities", "Strong safety standards", "Global networking environment"]
    },
    faq: [
      {
        q: "Is UAE a good destination for international students?",
        a: "Yes. It offers international campuses, modern facilities, and strong academic options across multiple fields."
      },
      {
        q: "Are scholarships available in UAE universities?",
        a: "Many institutions offer merit-based scholarships and fee waivers depending on profile and program."
      },
      {
        q: "Can students work while studying in UAE?",
        a: "Part-time work options depend on institution policy and current regulations. We guide you on the latest practical pathway."
      },
      {
        q: "How does NextEra support UAE applications?",
        a: "We support shortlisting, application submission, financial planning, and student visa readiness end-to-end."
      }
    ],
    finalCta: {
      title: "Plan Your UAE Study Path with Confidence",
      subtitle:
        "Get a personalized roadmap for UAE universities, applications, and visa readiness with NextEra Abroad.",
      primary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      secondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert }
    }
  },
  "new-zealand": {
    slug: "new-zealand",
    name: "New Zealand",
    flag: "🇳🇿",
    mapKey: "new-zealand",
    heroImage: siteConfig.images.destinations.newZealand,
    heroImageAlt: "New Zealand mountain landscape",
    seo: {
      title: "Study in New Zealand | NextEra Abroad",
      description:
        "Plan your New Zealand education journey with strategic counselling, admissions support, and visa guidance from NextEra Abroad."
    },
    mood: {
      gradient: "from-cyan-500/25 via-sky-300/20 to-emerald-200/25",
      chip: "bg-cyan-500/15 text-cyan-900 border-cyan-500/30",
      accentText: "text-cyan-700",
      mapGlow: "text-cyan-500"
    },
    hero: {
      headline: "Study in New Zealand for Quality Education and Balanced Living",
      subheadline:
        "New Zealand offers student-focused learning, globally respected degrees, and a safe, welcoming environment for international students.",
      ctaPrimary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      ctaSecondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert },
      supportCards: sharedSupportCards,
      quickFacts: [
        { label: "Education quality", value: "Globally Respected" },
        { label: "Intake cycles", value: "Feb / Jul" },
        { label: "Student environment", value: "Supportive" }
      ]
    },
    overview: {
      title: "Why Students Choose New Zealand",
      description:
        "Students prefer New Zealand for practical teaching methods, strong quality standards, and a safe, community-oriented lifestyle.",
      points: [
        {
          title: "High Academic Standards",
          text: "Universities are known for research quality and internationally recognized programs."
        },
        {
          title: "Practical Learning",
          text: "Course structures emphasize applied learning and real-world project exposure."
        },
        {
          title: "Safe and Welcoming",
          text: "Inclusive communities and a student-friendly social environment."
        },
        {
          title: "Strong Career Value",
          text: "Degrees carry global recognition across major industries and geographies."
        }
      ]
    },
    cost: {
      tuition: [
        {
          label: "Undergraduate Programs",
          value: "NZD 22,000 - 38,000 / year",
          note: "Depends on institution and field"
        },
        {
          label: "Postgraduate Programs",
          value: "NZD 26,000 - 45,000 / year",
          note: "Specialized programs can vary further"
        }
      ],
      living: [
        {
          label: "Monthly Living Expenses",
          value: "NZD 1,300 - 2,300 / month",
          note: "City, accommodation, and lifestyle dependent"
        },
        {
          label: "Transport + Essentials",
          value: "NZD 220 - 520 / month",
          note: "Approximate recurring budget"
        }
      ]
    },
    process: [
      {
        title: "Profile Review",
        text: "We evaluate academic profile, goals, and budget against New Zealand pathways."
      },
      {
        title: "University and Course Mapping",
        text: "Fit-based shortlist balancing quality, affordability, and long-term career relevance."
      },
      {
        title: "Application Preparation",
        text: "SOP strategy, document planning, and complete readiness support."
      },
      {
        title: "Submission and Tracking",
        text: "Application filing support with active timeline management."
      },
      {
        title: "Offer Decision Support",
        text: "Offer comparison with tuition, city, and career opportunity context."
      },
      {
        title: "Visa Guidance",
        text: "Structured support for financial proof, documentation, and visa filing clarity."
      }
    ],
    courses: [
      { name: "Information Technology", text: "Software, cloud systems, and applied digital skills" },
      { name: "Business & Management", text: "Management, strategy, and operations-focused pathways" },
      { name: "Engineering", text: "Civil, structural, environmental, and systems engineering" },
      { name: "Health Sciences", text: "Public health and allied healthcare-oriented programs" },
      { name: "Agriculture & Environmental Science", text: "Sustainability and agri-innovation pathways" },
      { name: "Hospitality & Tourism", text: "Service leadership and tourism management programs" }
    ],
    universities: [
      { name: "University of Auckland", city: "Auckland", text: "Top-ranked university with broad academic strengths." },
      { name: "University of Otago", city: "Dunedin", text: "Research-led institution with strong student community." },
      { name: "Victoria University of Wellington", city: "Wellington", text: "Known for policy, business, and technology pathways." },
      { name: "University of Canterbury", city: "Christchurch", text: "Strong engineering and applied sciences focus." },
      { name: "Massey University", city: "Palmerston North", text: "Well regarded for business, creative arts, and applied fields." },
      { name: "Auckland University of Technology", city: "Auckland", text: "Career-oriented learning with practical industry exposure." }
    ],
    career: {
      title: "Career Prospects in New Zealand",
      summary:
        "New Zealand pathways support graduates with practical skills and strong employability in multiple sectors.",
      points: [
        "Industry-oriented course design and project exposure",
        "Growth opportunities in technology, engineering, and services",
        "Supportive professional environment for international graduates",
        "Globally recognized degree value for long-term mobility"
      ]
    },
    lifestyle: {
      title: "Student Life in New Zealand",
      description:
        "Students enjoy a balanced lifestyle, nature-rich surroundings, and a supportive academic environment across major cities.",
      highlights: ["Safe communities", "Balanced lifestyle", "Outdoor-oriented culture", "Inclusive campuses"]
    },
    faq: [
      {
        q: "Is New Zealand good for international students?",
        a: "Yes. It is known for quality teaching, student support, and strong academic standards."
      },
      {
        q: "How expensive is studying in New Zealand?",
        a: "Costs vary by university and city. We help you estimate tuition and living budgets before you decide."
      },
      {
        q: "Can I work while studying in New Zealand?",
        a: "Part-time work options usually depend on your visa conditions and current policies. We guide you with the latest requirements."
      },
      {
        q: "How does NextEra help for New Zealand?",
        a: "We assist from profile mapping and shortlisting to application management and visa preparation."
      }
    ],
    finalCta: {
      title: "Start Your New Zealand Journey with Expert Guidance",
      subtitle:
        "Book personalized counselling to plan New Zealand admissions and visa readiness with confidence.",
      primary: { label: "Book Free Counselling", to: siteConfig.links.bookCounselling },
      secondary: { label: "Talk to an Expert", to: siteConfig.links.talkToExpert }
    }
  }
};

export const countryOrder = [
  "usa",
  "canada",
  "uk",
  "australia",
  "uae",
  "new-zealand",
  "europe",
  "ireland",
  "germany"
];

export function getCountryBySlug(slug) {
  return countryPages[slug];
}
